// Sub-task list in the task panel, hand-ordered by dragging the grip on each
// row (Matt Riley, 10/01: "add a step and drag it up above 'Post October's
// events…'"). Only the grip starts a drag, so a click on the row still opens
// the sub-task. The new order shows at once and is saved in the background;
// a refusal puts the old order back.

import { useRef } from 'react';
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import type { TaskDetail, TaskListItem } from '@cg-projects/shared';
import { Icon } from '@/core/ui/icons';
import { StateBadge } from '@/core/ui/atoms';
import { api } from '@/core/api/apiClient';
import { useToastStore } from '@/core/ui/toastStore';
import { useTaskDetailStore } from '../store/taskDetailStore';

const CLOSED = new Set(['1_done', '1_canceled']);

/** Swap the open task's children for `children` — only if that task is still
 *  the one open. Touches nothing else on the task, so a description being
 *  edited is never overwritten by a reorder. */
function setChildren(parentId: number, children: TaskListItem[]): void {
  useTaskDetailStore.setState((s) => (s.task && s.task.id === parentId ? { task: { ...s.task, children } } : s));
}

function SubtaskRow({ sub, onOpen }: { sub: TaskListItem; onOpen: (id: number) => void }) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: sub.id });
  return (
    <div
      ref={setNodeRef}
      // Up and down only: the list is one column, sideways travel means nothing.
      style={{ transform: CSS.Translate.toString(transform && { ...transform, x: 0 }), transition }}
      className={`group/sub relative flex items-center border-b border-line pl-1.5 last:border-0 hover:bg-hover ${isDragging ? 'z-10 bg-canvas shadow-md' : ''}`}
    >
      <button
        ref={setActivatorNodeRef}
        {...attributes}
        {...listeners}
        type="button"
        title="Drag to reorder"
        aria-label={`Reorder ${sub.name}`}
        className={`flex w-[22px] shrink-0 cursor-grab touch-none justify-center self-stretch items-center text-faint transition hover:text-ink focus-visible:opacity-100 active:cursor-grabbing pointer-coarse:opacity-100 ${isDragging ? 'opacity-100' : 'opacity-0 group-hover/sub:opacity-100'}`}
      >
        <Icon i={GripVertical} size={14} />
      </button>
      <button
        type="button"
        onClick={() => onOpen(sub.id)}
        className="flex min-w-0 flex-1 items-center justify-between gap-2 py-2 pl-1.5 pr-3 text-left text-[13px]"
      >
        <span className={`truncate ${CLOSED.has(sub.state) ? 'text-faint line-through' : 'text-ink'}`}>{sub.name}</span>
        {/* The name gives way, never the badge ("In Progress" broke in two on a phone). */}
        <span className="shrink-0 whitespace-nowrap"><StateBadge state={sub.state} /></span>
      </button>
    </div>
  );
}

export function SubtaskList({ parentId, subtasks, onOpen }: {
  parentId: number;
  subtasks: TaskListItem[];
  onOpen: (id: number) => void;
}) {
  const toast = useToastStore((s) => s.show);
  // Saves go out one at a time, in drag order, so two quick drags can never
  // land on the server backwards; only the newest drag's answer is applied.
  const chain = useRef<Promise<void>>(Promise.resolve());
  const latest = useRef(0);
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const before = subtasks;
    const from = before.findIndex((c) => c.id === active.id);
    const to = before.findIndex((c) => c.id === over.id);
    if (from < 0 || to < 0) return;
    const next = arrayMove(before, from, to);
    setChildren(parentId, next);
    const mine = ++latest.current;
    chain.current = chain.current.then(() =>
      api.post<{ task: TaskDetail }>(`/tasks/${parentId}/subtasks/reorder`, { orderedIds: next.map((c) => c.id) })
        .then(({ task }) => { if (mine === latest.current) setChildren(parentId, task.children); })
        .catch((err: unknown) => {
          if (mine === latest.current) setChildren(parentId, before);
          toast(err instanceof Error ? err.message : 'Could not save the new order.', 'error');
        }),
    );
  };

  if (!subtasks.length) return null;
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={subtasks.map((c) => c.id)} strategy={verticalListSortingStrategy}>
        {subtasks.map((c) => <SubtaskRow key={c.id} sub={c} onOpen={onOpen} />)}
      </SortableContext>
    </DndContext>
  );
}
